/**
 * Thumbs up / thumbs down vote state. Each vote is its own top-level `data`
 * key, so concurrent voters merge instead of overwriting one another.
 */
export type VoteValue = "up" | "down";

export interface StoredVote {
  name: string;
  avatarUrl?: string;
  at: number;
  value: VoteValue;
}

export interface Vote extends StoredVote {
  uid: string;
}

const PREFIX = "vote:";

export function voteKey(uid: string): string {
  return PREFIX + uid;
}

/** Votes in the order they were cast. A vote stored without a value is up. */
export function votesOf(data: Record<string, unknown>): Vote[] {
  const votes: Vote[] = [];
  for (const [key, value] of Object.entries(data)) {
    if (!key.startsWith(PREFIX) || !value || typeof value !== "object") {
      continue;
    }
    const v = value as Partial<StoredVote>;
    votes.push({
      uid: key.slice(PREFIX.length),
      name: typeof v.name === "string" ? v.name : "",
      ...(typeof v.avatarUrl === "string" && v.avatarUrl
        ? { avatarUrl: v.avatarUrl }
        : {}),
      at: typeof v.at === "number" ? v.at : 0,
      value: v.value === "down" ? "down" : "up",
    });
  }
  return votes.sort((a, b) => a.at - b.at || a.uid.localeCompare(b.uid));
}

export function tally(votes: readonly Vote[]): { up: number; down: number } {
  const up = votes.filter((v) => v.value === "up").length;
  return { up, down: votes.length - up };
}

/** The data with every vote removed, so a pasted copy starts at 0. */
export function withoutVotes(
  data: Record<string, unknown>,
): Record<string, unknown> {
  return Object.fromEntries(
    Object.entries(data).filter(([key]) => !key.startsWith(PREFIX)),
  );
}

/** The user's current vote, or null when they haven't voted. */
export function voteOf(
  data: Record<string, unknown>,
  uid: string,
): VoteValue | null {
  const v = data[voteKey(uid)];
  if (!v || typeof v !== "object") return null;
  return (v as Partial<StoredVote>).value === "down" ? "down" : "up";
}

/**
 * The one-key patch that casts `uid`'s vote. Casting the other side replaces
 * the user's own key and touches no one else's.
 */
export function castVote(
  uid: string,
  user: { name?: string; avatar?: string },
  value: VoteValue,
  now: number,
): Record<string, StoredVote> {
  return {
    [voteKey(uid)]: {
      name: user.name ?? "",
      ...(user.avatar ? { avatarUrl: user.avatar } : {}),
      at: now,
      value,
    },
  };
}
